/**
 * Use Case: Fetch Content By Tag
 *
 * Orchestrates fetching paginated media content filtered by a hashtag or content tag.
 * Part of clean architecture: Presentation → UseCase → Service → Firestore
 *
 * Matches Flutter's ContentTagsScreen -> MediasRepository.getContentByHashTag flow
 * including client-side filters from content_data_source.dart
 */

import { ContentTagService } from '@/domain/services/contentTag/contentTagService';
import { UserMediaRemapper } from '@/domain/remappers/userMedia/userMediaRemapper';
import type { PageCursor } from '@/data/types/json';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';
import type { UserMediasResponse } from '@/domain/apiResponses/medias/userMediasResponse';
import { getAuthFirebaseUserUseCase } from '@/domain/usecases/user/getAuthFirebaseUserUseCase';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { shouldFilterOutScheduledMediaUseCase, type HasActiveSubscriptionPredicate } from '@/domain/usecases/userMedia/shouldFilterOutScheduledMediaUseCase';
import { shouldFilterOutBlockedOrHiddenMediaUseCase } from '@/domain/usecases/userMedia/shouldFilterOutBlockedOrHiddenMediaUseCase';

export class FetchContentByTagUseCase {
    private contentTagService: ContentTagService;

    constructor() {
        this.contentTagService = ContentTagService.getInstance();
    }

    /**
     * Execute the use case
     *
     * @param tagName - The hashtag or content tag name
     * @param isHashtag - Whether this is a hashtag (true) or content tag (false)
     * @param currentUser - The authenticated user (from Redux) used for client-side
     *   filtering of blocked / hidden / scheduled content. Pass `null` if unavailable
     *   — filters that depend on it will be skipped.
     * @param hasActiveSubscription - Predicate `(creatorUserId) => boolean` sourced
     *   from the exclusiveContent slice. Use cases cannot import Redux directly,
     *   so hooks pass this in via useStore + selectHasActiveSubscription.
     * @param cursor - Pagination cursor
     * @param limit - Number of items to fetch (default: 9)
     * @returns Paginated user medias matching the tag
     */
    async execute(
        tagName: string,
        isHashtag: boolean,
        currentUser: UserEntity | null,
        hasActiveSubscription: HasActiveSubscriptionPredicate,
        cursor?: PageCursor,
        limit: number = SERVER_FETCH_CONSTANTS.DEFAULT_LIMIT
    ): Promise<UserMediasResponse> {
        try {
            const currentFirebaseUser = getAuthFirebaseUserUseCase.execute();

            const result = await this.contentTagService.getContentByTag(
                tagName,
                isHashtag,
                cursor,
                limit
            );

            let medias: UserMediaEntity[] = result.items.map(dto => UserMediaRemapper.toEntity(dto));

            // Client-side filters matching Flutter's content_data_source.dart

            // Filter out blocked-by-creator and hidden content (shared rule)
            medias = medias.filter(m => !shouldFilterOutBlockedOrHiddenMediaUseCase.execute(m, currentUser));

            // Filter out scheduled / early-access content for non-owners
            medias = medias.filter(m =>
                m.firebaseId === currentFirebaseUser?.uid ||
                !shouldFilterOutScheduledMediaUseCase.execute(m, currentFirebaseUser?.uid, hasActiveSubscription)
            );

            return {
                medias,
                hasMore: result.hasMore,
                cursor: result.cursor,
            };
        } catch (error) {
            throw error;
        }
    }
}

export const createFetchContentByTagUseCase = (): FetchContentByTagUseCase => {
    return new FetchContentByTagUseCase();
};
